import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Separator } from "@/components/ui/Separator";
import { Input } from "@/components/ui/Input";
import { Search } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { getStatusColor } from "@/utils/Helper";
import { Loader2 } from "lucide-react";

interface LinkedTicketsProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  tickets: any[];
  isLoading: boolean;
  selectedTicket: any;
  setSelectedTicket: (ticket: any) => void;
  onSave: () => void;
  loading: boolean;
  isTicketLinking?: boolean;
}

const LinkedTickets: React.FC<LinkedTicketsProps> = ({
  isOpen,
  setOpen,
  searchQuery,
  setSearchQuery,
  tickets,
  isLoading,
  selectedTicket,
  setSelectedTicket,
  onSave,
  loading,
  isTicketLinking = false,
}) => {
 const isSaving = loading || isTicketLinking;
  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setOpen(false);
          setSelectedTicket(null);
        } else {
          setOpen(open);
        }
      }}
    >
      <SheetContent className="!max-w-[550px] p-6">
        <SheetHeader className="mb-4">
          <SheetTitle>Link Tickets</SheetTitle>
          <div className="text-sm text-gray-500 flex gap-2 justify-between items-center">
            Link another ticket to the current ticket
            <Button
              className="text-black bg-primary hover:bg-primary/80"
              onClick={onSave}
              disabled={!selectedTicket || isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </SheetHeader>

        <Separator className="my-4" />

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 z-10 transform -translate-y-1/2 text-black h-4 w-4" />
          <Input
            placeholder="Search ticket"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            disabled={isSaving}
          />
        </div>

        <div className="space-y-3 mt-4 overflow-y-auto fancy-scroll max-h-[calc(100vh-250px)] p-6">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="p-3 animate-pulse">
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </Card>
              ))}
            </div>
          ) : tickets.length > 0 ? (
            tickets.map((ticket) => (
              <Card
                key={ticket.id}
                className={`p-3 cursor-pointer hover:border-primary transition-colors ${
                  selectedTicket?.id === ticket.id
                    ? "border-primary bg-primary/5"
                    : ""
                }`}
                onClick={() => !isSaving && setSelectedTicket(ticket)} // Prevent selection while saving
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium mb-1 text-nowrap flex flex-row">
                      <div className="flex flex-row items-center justify-between gap-3">
                        <span className="text-sm text-gray-500">
                          {ticket.ticket_number} - {ticket.ticket_id}
                        </span>
                      </div>
                    </h4>
                    <p className="font-medium mb-1 capitalize break-words whitespace-pre-wrap">
                      Title: {ticket.title}
                    </p>
                    <p className="text-sm capitalize text-gray-500 break-words whitespace-pre-wrap break-all max-w-[80%]">
                      Description: {ticket.description || "No description"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-base capitalize text-gray-500">
                    Service: {ticket.service?.name || "Unknown"}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-base capitalize text-gray-500">
                    <Badge
                      className={`${getStatusColor(ticket.status)} text-white`}
                    >
                      {ticket.status?.replace("_", " ")}
                    </Badge>
                  </span>
                </div>
              </Card>
            ))
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-500">No matching tickets found.</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default LinkedTickets;
